import React, { useMemo } from 'react';
import { Button, Select } from 'antd';

import SourceMark from '../notifications/SourceMark';
import { optionSearchFilter } from '../Input';
import { timezoneOptions } from './ScheduleFields';
import { describeCollectionTimezone } from '../../utils/collectionTimezone';

/**
 * Collection settings › Time zone (docs/plans/analytics.md §4.4, the owner's
 * ruling q06): the zone the collection's days, weeks and months are counted
 * in, so everyone looking at its charts sees the same days.
 *
 * The value cascades — this collection, else the one above it, else keepr's
 * default (US/Pacific) — so the label carries a `SourceMark` saying where the
 * zone came from (standards §9 item 9; "Inherited from {collection}", the
 * card layout editor's words), and a zone set here offers **Reset to
 * inherited**, with a line naming where that lands and the zone it gives.
 *
 * Inheriting, the picker holds NO value and shows the inherited zone as its
 * placeholder, so picking that same zone is a change and pins it here (a
 * picker holding it as its value would ignore the pick). The reset sends
 * `null`, which the API reads as "inherit". Both are saved by Save changes
 * with the rest of the tab. Read-only viewers see the value and its mark,
 * never the reset.
 */
export default function CollectionTimezoneField( { collection, onChange, readOnly = false } ) {
	const view = describeCollectionTimezone( collection );
	const ownZone = view?.ownZone;
	const inheritedZone = view?.inheritedZone;
	const options = useMemo( () => timezoneOptions( ownZone, inheritedZone ), [ ownZone, inheritedZone ] );

	if ( ! view ) return null;
	const resettable = ! readOnly && ( view.own || view.ignored );

	return (
		<div className='collection-timezone'>
			<div className='collection-timezone__label-row'>
				<label htmlFor='collection-timezone'>Time zone</label>
				<SourceMark id='collection-timezone-source' word={view.mark.word} phrase={view.mark.phrase} own={view.mark.own} />
			</div>
			<Select
				id='collection-timezone'
				size='large'
				showSearch
				filterOption={optionSearchFilter}
				className='collection-timezone__select'
				value={view.ownZone || undefined}
				placeholder={view.inheritedZone}
				options={options}
				onChange={next => onChange( 'timezone', next )}
				aria-describedby='collection-timezone-source collection-timezone-hint'
			/>
			<div id='collection-timezone-hint' className='form-panel__hint'>
				Charts count days, weeks and months in this time zone.
			</div>
			{view.ignored ? (
				<div className='form-panel__hint collection-timezone__ignored'>
					&ldquo;{view.ignored}&rdquo; is not a time zone keepr accepts, so this collection uses the one it inherits.
				</div>
			) : null}
			{resettable ? (
				<div className='collection-timezone__reset-row'>
					<Button
						type='link'
						className='collection-timezone__reset'
						aria-describedby='collection-timezone-fallback'
						onClick={() => onChange( 'timezone', null )}
					>
						Reset to inherited
					</Button>
					<span id='collection-timezone-fallback' className='collection-timezone__fallback'>{view.fallbackText}</span>
				</div>
			) : null}
		</div>
	);
}
